/** Resolution of the active workspace/account context: flag → env → config → token claim. */
import { loadConfig } from './config.js'
import { decodeJwtPayload } from './jwt.js'

export interface ContextFlags {
  workspace?: string
  account?: string
}

export async function resolveWorkspaceId(flags: ContextFlags, required = true): Promise<string | undefined> {
  const config = await loadConfig()
  const workspaceId = flags.workspace ?? process.env.VERENTIS_WORKSPACE_ID ?? config.defaultWorkspaceId
  if (!workspaceId && required) {
    throw new Error('No workspace selected. Pass --workspace <id> or set a default with `verentis use workspace <id>`.')
  }
  return workspaceId
}

export async function resolveAccountId(flags: ContextFlags, required = true): Promise<string | undefined> {
  const config = await loadConfig()
  let accountId = flags.account ?? process.env.VERENTIS_ACCOUNT_ID ?? config.defaultAccountId
  if (!accountId) {
    const identityToken = process.env.VERENTIS_TOKEN ?? config.identityToken
    if (identityToken) accountId = decodeJwtPayload(identityToken)?.account_id as string | undefined
  }
  if (!accountId && required) {
    throw new Error('No account selected. Pass --account <id> or set a default with `verentis use account <id>`.')
  }
  return accountId
}

/** Standard scope query params for Security management endpoints (users, api-keys, grants). */
export async function managementScope(flags: ContextFlags & { node?: string }): Promise<Record<string, string>> {
  const accountId = (await resolveAccountId(flags))!
  const workspaceId = await resolveWorkspaceId(flags, false)
  if (flags.node && !workspaceId) throw new Error('--node requires --workspace.')
  const scopeKind = flags.node ? 'Node' : workspaceId ? 'Workspace' : 'Account'
  const scopeId = flags.node ?? workspaceId ?? accountId
  return {
    scopeKind,
    scopeId,
    accountId,
    ...(workspaceId ? { workspaceId } : {}),
    ...(flags.node ? { nodeId: flags.node } : {}),
  }
}
